import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from './auth/firebaseConfig';
import { clearAllCookies, getCookie, setCookie } from '../utils/cookieUtils';
import {
  buildLicenseForPlan,
  computeCreditsRemaining,
  computeRolloverRemaining,
  PLANS,
} from '../utils/licenseUtils';
import {
  getLocalTenantLicenseSnapshot,
  syncTenantLicense,
} from '../lib/centralTenantApi';
import { buildCcsPricingUrl } from '../utils/ccsNavigation';

const DEFAULT_LICENSE_PLAN = 'Professional';
const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatExpiryDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'shortOffset',
  });
};

const resolveTenantName = (tenant, tenantId) => (
  tenant?.companyName
  || tenant?.tenantName
  || tenant?.organizationName
  || tenantId
  || 'Organization'
);

const resolveTenantLogo = (tenant, fallbackLogoUrl) => (
  tenant?.secondaryLogoUrl
  || tenant?.companyLogoUrl
  || fallbackLogoUrl
  || '/logo.png'
);

const getTenantLicense = (tenant) => {
  if (!tenant) return null;
  const plan = tenant?.license?.plan || tenant?.plan || tenant?.planId || DEFAULT_LICENSE_PLAN;
  return tenant?.license || buildLicenseForPlan(plan, { planAssignedBy: 'profile-fallback' });
};

const getTenantCreditSummary = (tenant) => {
  if (tenant?.centralLicense) {
    const used = Number(tenant.centralLicense.usedCredits || 0);
    const remaining = Number(tenant.centralLicense.remainingCredits || 0);
    return {
      available: remaining,
      total: Math.max(remaining, remaining + used),
      expiry: null,
      planId: tenant.centralLicense.planId,
    };
  }

  const license = getTenantLicense(tenant);
  if (!license) {
    return {
      available: 0,
      total: 0,
      expiry: null,
    };
  }

  const planConfig = PLANS[license?.plan] || PLANS.Trial;
  const total = license?.currentCycle?.creditsTotal ?? planConfig.credits ?? 0;
  const available = computeCreditsRemaining(license);
  const rolloverAvailable = computeRolloverRemaining(license);
  const expiry = rolloverAvailable > 0
    ? license?.rollover?.expiresAt
    : license?.currentCycle?.cycleEnd;

  return {
    available,
    total,
    expiry,
  };
};

const ProfileAvatar = ({
  tenantLogoUrl = '',
  tenantOrganizationName = '',
  showCreditsShortcut = false,
  showCreditsLabel = true,
  showCreditValue = false,
}) => {
  const navigate = useNavigate();
  const userData = getCookie('userData');
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState('credits');
  const [tenant, setTenant] = useState(null);
  const [loadingTenant, setLoadingTenant] = useState(false);
  const [tenantMemberships, setTenantMemberships] = useState([]);
  const [loadingMemberships, setLoadingMemberships] = useState(false);
  const [switchingTenantId, setSwitchingTenantId] = useState('');

  const tenantId = userData?.tenantId || userData?.selectedTenantId || '';
  const isMenuOpen = Boolean(anchorEl);

  const tenantName = resolveTenantName(tenant, tenantOrganizationName || tenantId);
  const tenantLogo = resolveTenantLogo(tenant, tenantLogoUrl);
  const creditSummary = useMemo(() => getTenantCreditSummary(tenant), [tenant]);
  const availableCredits = Math.max(0, creditSummary.available || 0);
  const totalCredits = Math.max(0, creditSummary.total || 0);
  const creditPercent = totalCredits > 0 ? Math.min(100, (availableCredits / totalCredits) * 100) : 0;

  useEffect(() => {
    if (!tenantId || (!isMenuOpen && !showCreditsShortcut)) return;

    let isMounted = true;
    const loadTenant = async () => {
      setLoadingTenant(true);
      try {
        const localLicense = await getLocalTenantLicenseSnapshot(tenantId);
        if (localLicense && isMounted) {
          setTenant((prev) => ({
            ...(prev || {}),
            tenantId,
            centralLicense: localLicense,
          }));
        }

        const syncedLicense = await syncTenantLicense(tenantId);
        if (syncedLicense?.success && syncedLicense.license && isMounted) {
          setTenant((prev) => ({
            ...(prev || {}),
            tenantId,
            centralLicense: syncedLicense.license,
          }));
        }

        const directSnap = await getDoc(doc(db, 'tenant', tenantId));
        if (directSnap.exists()) {
          if (isMounted) {
            setTenant((prev) => ({
              ...(prev || {}),
              id: directSnap.id,
              ...directSnap.data(),
              centralLicense: prev?.centralLicense,
            }));
          }
          return;
        }

        const tenantQuery = query(collection(db, 'tenant'), where('tenantId', '==', tenantId));
        const tenantSnap = await getDocs(tenantQuery);
        if (!tenantSnap.empty) {
          const tenantDoc = tenantSnap.docs[0];
          if (isMounted) {
            setTenant((prev) => ({
              ...(prev || {}),
              id: tenantDoc.id,
              ...tenantDoc.data(),
              centralLicense: prev?.centralLicense,
            }));
          }
        }
      } catch (error) {
        console.error('Unable to load tenant credits:', error);
      } finally {
        if (isMounted) setLoadingTenant(false);
      }
    };

    loadTenant();
    return () => {
      isMounted = false;
    };
  }, [tenantId, isMenuOpen, showCreditsShortcut]);

  useEffect(() => {
    if (!userData?.email || !isMenuOpen) return;

    let isMounted = true;
    const loadMemberships = async () => {
      setLoadingMemberships(true);
      try {
        const emailLower = userData.email.toLowerCase();
        const usersQuery = query(collection(db, 'users'), where('email', '==', emailLower));
        const usersSnapshot = await getDocs(usersQuery);
        const tenantDocsSnapshot = await getDocs(collection(db, 'tenant'));
        const tenantMap = new Map(
          tenantDocsSnapshot.docs.map((tenantDoc) => {
            const data = tenantDoc.data() || {};
            return [data.tenantId || tenantDoc.id, { id: tenantDoc.id, ...data }];
          })
        );

        const membershipMap = new Map();
        usersSnapshot.docs.forEach((userDoc) => {
          const data = userDoc.data() || {};
          const membershipTenantId = (data.tenantId || '').trim();
          if (!membershipTenantId || membershipMap.has(membershipTenantId)) return;

          const membershipTenant = tenantMap.get(membershipTenantId);
          if (!membershipTenant) return;

          membershipMap.set(membershipTenantId, {
            id: userDoc.id,
            userId: data.userId || userDoc.id,
            email: data.email || emailLower,
            userName: data.userName || data.email || emailLower,
            role: data.role || 'Admin',
            assignedGroups: data.assignedGroups || [],
            tenantId: membershipTenantId,
            tenantName: resolveTenantName(membershipTenant, membershipTenantId),
            tenantLogoUrl: resolveTenantLogo(membershipTenant, ''),
            status: membershipTenant.status || 'active',
          });
        });

        if (isMounted) {
          setTenantMemberships(Array.from(membershipMap.values()));
        }
      } catch (error) {
        console.error('Unable to load tenant memberships:', error);
        if (isMounted) setTenantMemberships([]);
      } finally {
        if (isMounted) setLoadingMemberships(false);
      }
    };

    loadMemberships();
    return () => {
      isMounted = false;
    };
  }, [userData?.email, isMenuOpen]);

  if (!userData || !userData.email) {
    console.error('User data not found in cookies.');
    return <Avatar>?</Avatar>;
  }

  const initials = userData.email.charAt(0).toUpperCase();

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setActiveTab('credits');
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      clearAllCookies();
      handleClose();
      navigate('/');
    }
  };

  const handlePricingClick = () => {
    handleClose();
    window.location.assign(buildCcsPricingUrl({
      tenantId,
      returnUrl: window.location.href,
    }));
  };

  const handleTenantSwitch = (membership) => {
    if (!membership?.tenantId || membership.tenantId === tenantId) return;

    setSwitchingTenantId(membership.tenantId);
    const nextUserData = {
      ...userData,
      email: membership.email || userData.email,
      userId: membership.userId,
      userName: membership.userName,
      role: membership.role,
      assignedGroups: membership.assignedGroups || [],
      tenantId: membership.tenantId,
      selectedTenantId: membership.tenantId,
      tenantName: membership.tenantName,
    };

    setCookie('userData', nextUserData);
    window.location.assign('/project-page');
  };

  return (
    <>
      <Stack direction="row" spacing={1.15} alignItems="center">
        {showCreditsShortcut && showCreditsLabel && (
          <Button
            onClick={handleOpen}
            startIcon={<FormatListBulletedRoundedIcon />}
            sx={{
              color: '#9ca3b6',
              textTransform: 'none',
              fontWeight: 800,
              fontSize: { xs: '0.92rem', md: '1rem' },
              px: 1.1,
              minWidth: 'auto',
              '& .MuiButton-startIcon': {
                mr: 0.65,
                color: '#7cc47f',
              },
              '&:hover': {
                color: '#eef4ff',
                backgroundColor: 'transparent',
              },
            }}
          >
            Credits
          </Button>
        )}

        <Tooltip title={userData.email}>
          <Avatar
            onClick={handleOpen}
            sx={{
              bgcolor: '#4f86df',
              width: showCreditsShortcut ? { xs: 42, sm: 48 } : 40,
              height: showCreditsShortcut ? { xs: 42, sm: 48 } : 40,
              cursor: 'pointer',
              fontWeight: 800,
              fontSize: showCreditsShortcut ? { xs: '1.05rem', sm: '1.3rem' } : '1rem',
              boxShadow: showCreditsShortcut ? '0 8px 22px rgba(79, 134, 223, 0.28)' : 'none',
            }}
          >
            {showCreditValue ? formatNumber(availableCredits) : initials}
          </Avatar>
        </Tooltip>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1.2,
            width: { xs: 'calc(100vw - 24px)', sm: 430 },
            maxWidth: 'calc(100vw - 24px)',
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 22px 60px rgba(15, 23, 42, 0.20)',
          },
        }}
      >
        <Stack direction="row" sx={{ px: 1, pt: 1 }}>
          <MenuItem
            selected={activeTab === 'credits'}
            onClick={() => setActiveTab('credits')}
            sx={{ flex: 1, borderRadius: 2, fontWeight: 800 }}
          >
            <ListItemIcon sx={{ minWidth: 34 }}>
              <AccountBalanceWalletRoundedIcon fontSize="small" color="primary" />
            </ListItemIcon>
            Credits
          </MenuItem>
          <MenuItem
            selected={activeTab === 'switching'}
            onClick={() => setActiveTab('switching')}
            sx={{ flex: 1, borderRadius: 2, fontWeight: 800 }}
          >
            <ListItemIcon sx={{ minWidth: 34 }}>
              <SwapHorizRoundedIcon fontSize="small" color="primary" />
            </ListItemIcon>
            Switching
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ flex: 1, borderRadius: 2, fontWeight: 800 }}>
            <ListItemIcon sx={{ minWidth: 34 }}>
              <LogoutRoundedIcon fontSize="small" color="error" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Stack>

        {activeTab === 'credits' && (
          <Box sx={{ px: 2.2, pt: 1.4, pb: 2.2 }}>
            <Box
              sx={{
                borderRadius: 4,
                backgroundColor: '#fff',
                border: '1px solid rgba(226, 232, 240, 0.95)',
                boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
                overflow: 'hidden',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ px: 2, py: 1.65 }}
              >
                <Avatar
                  src={tenantLogo}
                  variant="rounded"
                  sx={{
                    width: 38,
                    height: 38,
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#111827',
                    fontWeight: 800,
                  }}
                >
                  {tenantName.charAt(0).toUpperCase()}
                </Avatar>
                <Typography
                  noWrap
                  sx={{
                    flex: 1,
                    color: '#5d6472',
                    fontSize: '1rem',
                    fontWeight: 800,
                  }}
                >
                  {tenantName}
                </Typography>
                {loadingTenant ? (
                  <CircularProgress size={20} />
                ) : (
                  <Typography
                    sx={{
                      color: '#4f9d67',
                      whiteSpace: 'nowrap',
                      fontSize: '1rem',
                      fontWeight: 900,
                    }}
                  >
                    {formatNumber(availableCredits)} available / {formatNumber(totalCredits)}
                  </Typography>
                )}
              </Stack>

              <Box sx={{ px: 2, pb: 1.8 }}>
                <LinearProgress
                  variant="determinate"
                  value={creditPercent}
                  sx={{
                    height: 9,
                    borderRadius: 999,
                    backgroundColor: '#dceee0',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      backgroundColor: '#62b274',
                    },
                  }}
                />
              </Box>

              <Divider />

              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ px: 2, py: 1.8 }}
              >
                <Typography sx={{ flex: 1, color: '#8992a3', fontSize: '0.92rem', fontWeight: 700 }}>
                  {formatNumber(availableCredits)} credits expire
                  {creditSummary.expiry ? ` on ${formatExpiryDate(creditSummary.expiry)}.` : ' at the end of the billing cycle.'}
                </Typography>
                <InfoOutlinedIcon sx={{ color: '#94a3b8' }} />
              </Stack>

              <Divider />

              <Stack direction="row" justifyContent="flex-end" sx={{ px: 2, py: 1.8 }}>
                <Button
                  variant="contained"
                  onClick={handlePricingClick}
                  sx={{
                    px: 4,
                    py: 1,
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 900,
                    fontSize: '1rem',
                    background: '#3d65d8',
                    boxShadow: '0 10px 22px rgba(61, 101, 216, 0.24)',
                    '&:hover': {
                      background: '#2f55c7',
                    },
                  }}
                >
                  Pricing
                </Button>
              </Stack>
            </Box>
          </Box>
        )}

        {activeTab === 'switching' && (
          <Box sx={{ px: 2.2, pt: 1.4, pb: 2.2 }}>
            <Box
              sx={{
                borderRadius: 4,
                backgroundColor: '#fff',
                border: '1px solid rgba(226, 232, 240, 0.95)',
                boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 2, py: 1.6 }}>
                <Typography sx={{ fontWeight: 900, color: '#0f172a', mb: 0.4 }}>
                  Switch tenant
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: '0.86rem' }}>
                  Select the workspace you want to continue with.
                </Typography>
              </Box>
              <Divider />

              {loadingMemberships ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography sx={{ mt: 1.2, color: '#64748b', fontSize: '0.86rem' }}>
                    Loading tenants...
                  </Typography>
                </Stack>
              ) : tenantMemberships.length === 0 ? (
                <Box sx={{ px: 2, py: 2 }}>
                  <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
                    No other tenant access found for this account.
                  </Typography>
                </Box>
              ) : (
                <Stack sx={{ py: 0.75 }}>
                  {tenantMemberships.map((membership) => {
                    const isCurrent = membership.tenantId === tenantId;
                    const isSwitching = switchingTenantId === membership.tenantId;

                    return (
                      <Button
                        key={membership.tenantId}
                        onClick={() => handleTenantSwitch(membership)}
                        disabled={isCurrent || isSwitching}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          mx: 1,
                          my: 0.35,
                          px: 1.2,
                          py: 1,
                          borderRadius: 2,
                          backgroundColor: isCurrent ? '#f0f7ff' : 'transparent',
                          '&:hover': {
                            backgroundColor: '#f8fbff',
                          },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.3} sx={{ width: '100%' }}>
                          <Avatar
                            src={membership.tenantLogoUrl}
                            variant="rounded"
                            sx={{
                              width: 38,
                              height: 38,
                              bgcolor: '#e2e8f0',
                              color: '#0f172a',
                              fontWeight: 800,
                            }}
                          >
                            {membership.tenantName.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                            <Typography noWrap sx={{ color: '#172033', fontWeight: 850 }}>
                              {membership.tenantName}
                            </Typography>
                            <Typography noWrap sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                              {membership.tenantId} - {membership.role}
                            </Typography>
                          </Box>
                          {isSwitching ? (
                            <CircularProgress size={18} />
                          ) : (
                            <Typography sx={{ color: isCurrent ? '#2563eb' : '#94a3b8', fontSize: '0.78rem', fontWeight: 800 }}>
                              {isCurrent ? 'Current' : 'Switch'}
                            </Typography>
                          )}
                        </Stack>
                      </Button>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default ProfileAvatar;
